import axios from "axios";

// Backend error body (vault "API 규약"): { success: false, data: null, error: { code, message, message_id?, details? } }.
// Older endpoints may still send `error` as a string or only FastAPI's `detail`.
export interface ApiErrorDetail {
  field: string;
  message: string;
}

export interface ApiErrorBody {
  code: string;
  message: string;
  message_id?: string;
  details?: ApiErrorDetail[];
}

export const DEFAULT_ERROR_MESSAGE = "요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요.";

export function getApiError(error: unknown): ApiErrorBody | null {
  if (!axios.isAxiosError(error) || !error.response) return null;
  const body = error.response.data as { error?: unknown; detail?: unknown } | undefined;
  const raw = body?.error;

  if (raw && typeof raw === "object" && "message" in raw) {
    const obj = raw as Partial<ApiErrorBody>;
    return {
      code: obj.code ?? `HTTP_${error.response.status}`,
      message: obj.message ?? DEFAULT_ERROR_MESSAGE,
      message_id: obj.message_id,
      details: obj.details,
    };
  }
  if (typeof raw === "string" && raw) return { code: `HTTP_${error.response.status}`, message: raw };
  if (typeof body?.detail === "string" && body.detail) return { code: `HTTP_${error.response.status}`, message: body.detail };
  return null;
}

export function getApiErrorMessage(error: unknown, fallback: string = DEFAULT_ERROR_MESSAGE): string {
  return getApiError(error)?.message ?? fallback;
}

// HTTP status of a failed request (undefined when no response arrived: network down, aborted ...)
export function getApiStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

// The server refused the credentials themselves (401 expired / revoked session, 403 account no longer allowed): the session is gone.
// A network error, timeout, abort or 5xx says nothing about the session, so it must never log the user out.
export function isAuthError(error: unknown): boolean {
  const status = getApiStatus(error);
  return status === 401 || status === 403;
}

// The address / id does not exist or is not valid (404 · 400 · 410 · 422) -> "not found" screens.
// Everything else (5xx, no response, 401 session expired, ...) is a failed load that is worth retrying.
const NOT_FOUND_STATUSES = [400, 404, 410, 422];
export function isNotFoundError(error: unknown): boolean {
  const status = getApiStatus(error);
  return status !== undefined && NOT_FOUND_STATUSES.includes(status);
}

// Field errors keyed by field name, for forms ({ email: "…" }).
export function getApiFieldErrors(error: unknown): Record<string, string> {
  const details = getApiError(error)?.details ?? [];
  return Object.fromEntries(details.map((d) => [d.field, d.message]));
}
